import { useState } from 'react';
import { Plus } from 'lucide-react';
import { addContactChannel, type WebContactChannel } from '../../../lib/contact-book';
import { Button, Input, TextButton } from '../ui';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useTranslation } from '../../../lib/i18n';
import styles from './contacts.module.css';

/**
 * A "+" row for each contact detail this person does not have yet.
 *
 * Only the MISSING kinds get a row — a contact with an email but no phone is offered the phone and
 * nothing else, because the point is to complete the record rather than to collect more of what is
 * already there. Nothing is offered when both are present.
 *
 * The write unions server-side (`addEmails`/`addPhones`), so adding a phone cannot displace the
 * email already on file. See `pages/api/contact-channel.ts` for why this talks to the People
 * service directly rather than through symphony-bff.
 */
export function AddChannelRows({
  personId,
  channels,
}: {
  personId: string;
  channels: WebContactChannel[];
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [open, setOpen] = useState<'email' | 'phone' | null>(null);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const has = (kind: string) => channels.some((channel) => channel.kind === kind);
  const missing = (['email', 'phone'] as const).filter((kind) => !has(kind));
  if (missing.length === 0) return null;

  const submit = async (kind: 'email' | 'phone') => {
    const trimmed = value.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setFailed(false);
    const ok = await addContactChannel(personId, kind === 'email' ? { email: trimmed } : { phone: trimmed });
    setBusy(false);
    if (!ok) {
      // Kept open with the typed value intact — a failed save that clears the field makes the
      // person type it twice.
      setFailed(true);
      return;
    }
    setOpen(null);
    setValue('');
  };

  return (
    <>
      {missing.map((kind) =>
        open === kind ? (
          <div key={kind} className={styles.addChannelRow}>
            <Input
              value={value}
              onChange={(event) => setValue(event.target.value)}
              type={kind === 'email' ? 'email' : 'tel'}
              label={t(`contacts.detail.add.${kind}`)}
              status={failed ? 'error' : 'default'}
              statusMessage={failed ? t('contacts.detail.add.failed') : undefined}
              autoFocus
            />
            <div className={styles.addChannelActions}>
              <Button variant="outlined" size="small" onClick={() => { setOpen(null); setValue(''); setFailed(false); }}>
                {t('common.cancel')}
              </Button>
              <Button size="small" loading={busy} onClick={() => void submit(kind)}>
                {t('common.save')}
              </Button>
            </div>
          </div>
        ) : (
          <div key={kind} className={styles.addChannelTrigger}>
            {/* The DS TextButton, not a hand-rolled row and not `Button variant="text"` — the ghost
                Type is a padded box, where this is a line of words on a list row. */}
            <TextButton
              size="small"
              startIcon={<Plus size={16} aria-hidden />}
              onClick={() => {
                logPageClickBi({ page_name: 'contact_detail', button_name: `add_${kind}` });
                setOpen(kind);
                setValue('');
                setFailed(false);
              }}
            >
              {t(`contacts.detail.add.${kind}`)}
            </TextButton>
          </div>
        ),
      )}
    </>
  );
}
